import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ChallengeWithEligibility, getChallengeTimeStatus, getChallengeTimingMessage } from '@fitzaa/shared';
import { ChallengeTag } from './ChallengeTag';
import VideoPlayer from './VideoPlayer';
import { useNavigate } from 'react-router-dom';

interface ChallengeCardProps {
  challenge: ChallengeWithEligibility;
  onJoin?: (challengeId: string) => void;
  onViewProgress?: (challengeId: string) => void;
  isActive?: boolean;
  progress?: number; // Add progress prop
  participation?: any; // Add participation data for today's progress
  /**
   * Optional override handler for video play.
   * When provided, the card will call this instead of showing its own modal.
   */
  onVideoPlayOverride?: (challenge: ChallengeWithEligibility) => void;
}

const ChallengeCard: React.FC<ChallengeCardProps> = React.memo(({
  challenge,
  onJoin,
  onViewProgress,
  isActive = false,
  progress = 0, // Default to 0
  participation = null, // Default to null
  onVideoPlayOverride
}) => {
  const { theme } = useTheme();
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const navigate = useNavigate();

  // Detect mobile screen size
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleAction = useCallback(() => {
    if (isActive && onViewProgress) {
      onViewProgress(challenge.id);
    } else if (!isActive && onJoin) {
      onJoin(challenge.id);
    }
  }, [isActive, onViewProgress, onJoin, challenge.id]);

  const handleVideoPlay = useCallback(() => {
    if (onVideoPlayOverride) {
      onVideoPlayOverride(challenge);
      return;
    }
    setShowVideoPlayer(true);
  }, [challenge, onVideoPlayOverride]);

  const handleVideoClose = useCallback(() => {
    setShowVideoPlayer(false);
  }, []);

  const handlePhotoWallClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/challenge/${challenge.id}/photos`);
  }, [navigate, challenge.id]);

  // Memoize challenge time status and timing message
  const timeStatus = useMemo(() =>
    getChallengeTimeStatus(challenge.startDate, challenge.endDate),
    [challenge.startDate, challenge.endDate]
  );

  const timingMessage = useMemo(() => {
    const message = getChallengeTimingMessage(challenge.startDate, challenge.endDate);
    // Replace "remaining" with "left" on mobile for shorter text
    return isMobile ? message.replace(/remaining/gi, 'left') : message;
  }, [challenge.startDate, challenge.endDate, isMobile]);

  // Calculate today's progress percentage
  const todayProgress = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayCompletion = participation?.dailyCompletions?.[today];

    // Use the same logic as ChallengeProgressView
    const targetValue = (challenge as any).measurementTarget || challenge.targetValue || 1;
    // Only use today's specific value, not the cumulative currentValue
    const currentValue = todayCompletion?.value || 0;

    return targetValue > 0 ? Math.min(100, (currentValue / targetValue) * 100) : 0;
  }, [participation?.dailyCompletions, participation?.currentValue, challenge.targetValue]);

  // Memoize overall progress calculation
  const overallProgress = useMemo(() => {
    // Use the stored participation progress (points-based calculation)
    // This ensures consistency with the Firebase function and leaderboards
    if (participation?.progress !== undefined) {
      return participation.progress * 100;
    }

    // Fallback to the progress prop if participation data is not available
    return progress * 100;
  }, [participation?.progress, progress]);

  // Animated progress values for circular indicators
  const [animatedTodayProgress, setAnimatedTodayProgress] = useState(0);
  const [animatedOverallProgress, setAnimatedOverallProgress] = useState(0);

  useEffect(() => {
    const duration = 700; // ms
    const startTime = performance.now();
    const startToday = animatedTodayProgress;
    const startOverall = animatedOverallProgress;
    const targetToday = todayProgress;
    const targetOverall = overallProgress;

    let frameId: number;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const easeOut = 1 - Math.pow(1 - t, 3);

      setAnimatedTodayProgress(startToday + (targetToday - startToday) * easeOut);
      setAnimatedOverallProgress(startOverall + (targetOverall - startOverall) * easeOut);

      if (t < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayProgress, overallProgress]);

  // Combined circular progress: outer ring = overall, inner ring = today
  const renderCombinedCircularProgress = () => {
    // Larger chart for better readability of the points label and dual rings
    const outerRadius = isMobile ? 40 : 52;
    const outerStroke = isMobile ? 6 : 8;
    const innerRadius = isMobile ? 30 : 40;
    const innerStroke = isMobile ? 5 : 7;
    const size = isMobile ? 90 : 120;

    const outerNorm = outerRadius - outerStroke / 2;
    const innerNorm = innerRadius - innerStroke / 2;

    const outerCirc = 2 * Math.PI * outerNorm;
    const innerCirc = 2 * Math.PI * innerNorm;

    const overallClamped = Math.max(0, Math.min(100, animatedOverallProgress || 0));
    const todayClamped = Math.max(0, Math.min(100, animatedTodayProgress || 0));

    const outerOffset = outerCirc - (overallClamped / 100) * outerCirc;
    const innerOffset = innerCirc - (todayClamped / 100) * innerCirc;

    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <svg
          width={size}
          height={size}
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Outer track (overall baseline) */}
          <circle
            stroke={theme.colors.border}
            fill="transparent"
            strokeWidth={outerStroke}
            r={outerNorm}
            cx={size / 2}
            cy={size / 2}
          />
          {/* Outer ring - Overall */}
          <circle
            stroke={theme.colors.primary}
            fill="transparent"
            strokeWidth={outerStroke}
            strokeLinecap="round"
            r={outerNorm}
            cx={size / 2}
            cy={size / 2}
            strokeDasharray={`${outerCirc} ${outerCirc}`}
            strokeDashoffset={outerOffset}
            style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
          />

          {/* Inner track (today baseline) */}
          <circle
            stroke={theme.colors.border}
            fill="transparent"
            strokeWidth={innerStroke}
            r={innerNorm}
            cx={size / 2}
            cy={size / 2}
          />
          {/* Inner ring - Today */}
          <circle
            stroke={theme.colors.success}
            fill="transparent"
            strokeWidth={innerStroke}
            strokeLinecap="round"
            r={innerNorm}
            cx={size / 2}
            cy={size / 2}
            strokeDasharray={`${innerCirc} ${innerCirc}`}
            strokeDashoffset={innerOffset}
            style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
          />
        </svg>
        {/* Points in the center */}
        <div
          style={{
            position: 'absolute',
            textAlign: 'center',
            pointerEvents: 'none',
            color: theme.colors.text,
            fontFamily: theme.fonts.secondary,
          }}
        >
          <div style={{ fontSize: isMobile ? 10 : 12, opacity: 0.7 }}>Points</div>
          <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700 }}>
            +{challenge.points || 0}
          </div>
        </div>
      </div>
    );
  };

  // Memoize button click handler
  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleAction();
  }, [handleAction]);

  // Debug logging - only in development
  if (isActive && process.env.NODE_ENV === 'development') {
    console.log('🔍 ChallengeCard Progress Debug:', {
      challengeId: challenge.id,
      progress: progress,
      progressPercentage: Math.round(progress * 100),
      todayProgress: todayProgress,
      overallProgress: overallProgress,
      participation: participation,
      dailyCompletions: participation?.dailyCompletions,
      today: new Date().toISOString().split('T')[0]
    });
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      backgroundColor: theme.colors.card,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: isMobile ? '12px' : '18px',
      padding: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '8px' : '16px',
      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: 'pointer',
      fontFamily: theme.fonts.primary,
      width: '100%',
      boxSizing: 'border-box'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
      onClick={handleAction}
    >
      {/* Content */}
      <div style={{ flex: 1 }}>
        {/* Top row: text (≈70%) and combined circular progress (≈30%) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: isMobile ? 10 : 16,
            marginBottom: isActive ? (isMobile ? 8 : 8) : 4,
          }}
        >
          {/* Text column */}
          <div style={{ flex: 7, minWidth: 0 }}>
            {/* Title with inline emoji */}
            <h3 style={{
              color: theme.colors.text,
              margin: '0 0 4px 0',
              fontSize: isMobile ? '14px' : theme.typography.h3.fontSize,
              fontWeight: theme.typography.h3.fontWeight,
              fontFamily: theme.fonts.display,
              lineHeight: isMobile ? '1.3' : theme.typography.h3.lineHeight,
              letterSpacing: theme.typography.h3.letterSpacing,
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '8px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}>
              <span style={{ fontSize: isMobile ? '18px' : '24px', flexShrink: 0 }}>{challenge.emoji || '🎯'}</span>
              <span style={{
                flex: 1,
                minWidth: 0,
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                overflow: isMobile ? 'visible' : 'hidden',
                textOverflow: isMobile ? 'clip' : 'ellipsis',
                wordWrap: isMobile ? 'break-word' : 'normal',
                overflowWrap: isMobile ? 'break-word' : 'normal'
              }}>
                {challenge.title}
              </span>
              {/* Show days-left pill next to title only on larger screens */}
              {!isMobile && timeStatus.status === 'active' && timeStatus.daysRemaining > 0 && (
                <span
                  style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    borderRadius: '999px',
                    fontSize: theme.typography.caption.fontSize,
                    fontWeight: 600,
                    fontFamily: theme.fonts.secondary,
                    letterSpacing: theme.typography.caption.letterSpacing,
                    color: theme.colors.warning,
                    backgroundColor: 'rgba(250, 204, 21, 0.15)',
                    flexShrink: 0,
                  }}
                >
                  {timingMessage}
                </span>
              )}
            </h3>

            {/* Description */}
            <p style={{
              color: theme.colors.textSecondary,
              margin: '0',
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              letterSpacing: theme.typography.body.letterSpacing,
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 2 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {challenge.description}
            </p>

            {/* On mobile, show the days-left pill below the description to avoid wrapping the title */}
            {isMobile && timeStatus.status === 'active' && timeStatus.daysRemaining > 0 && (
              <div
                style={{
                  marginTop: 4,
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
              >
                <span
                  style={{
                    padding: '2px 6px',
                    borderRadius: '999px',
                    fontSize: theme.typography.caption.fontSize,
                    fontWeight: 600,
                    fontFamily: theme.fonts.secondary,
                    letterSpacing: theme.typography.caption.letterSpacing,
                    color: theme.colors.warning,
                    backgroundColor: 'rgba(250, 204, 21, 0.15)',
                  }}
                >
                  {timingMessage}
                </span>
              </div>
            )}
            {/* End date, duration, and organization tags under description */}
            <div
              style={{
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              {timeStatus.hasValidDates && (
                <span
                  style={{
                    fontSize: isMobile ? '10px' : theme.typography.caption.fontSize,
                    fontFamily: theme.fonts.secondary,
                    color: theme.colors.primary,
                    fontWeight: 500,
                  }}
                >
                  Ends {timeStatus.formattedEndDate}
                </span>
              )}
              {/* Duration Badge (daily / weekly / monthly) */}
              {(challenge as any).duration && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: isMobile ? '2px 6px' : '2px 10px',
                  borderRadius: '999px',
                  fontSize: isMobile ? '9px' : theme.typography.caption.fontSize,
                  fontWeight: 600,
                  fontFamily: theme.fonts.secondary,
                  letterSpacing: theme.typography.caption.letterSpacing,
                  textTransform: 'capitalize',
                  backgroundColor: (challenge as any).duration === 'daily'
                    ? 'rgba(59, 130, 246, 0.12)'
                    : (challenge as any).duration === 'weekly'
                      ? 'rgba(139, 92, 246, 0.14)'
                      : 'rgba(239, 68, 68, 0.12)',
                  color: (challenge as any).duration === 'daily'
                    ? '#2563eb'
                    : (challenge as any).duration === 'weekly'
                      ? '#7c3aed'
                      : '#ef4444',
                }}>
                  {(challenge as any).duration}
                </span>
              )}
              {/* Organization tag, following duration */}
              {((challenge as any).organizationName || (challenge as any).organizationId) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: isMobile ? '2px 6px' : '2px 10px',
                    borderRadius: '999px',
                    fontSize: isMobile ? '9px' : theme.typography.caption.fontSize,
                    fontWeight: 600,
                    fontFamily: theme.fonts.secondary,
                    letterSpacing: theme.typography.caption.letterSpacing,
                    textTransform: 'capitalize',
                    backgroundColor: 'rgba(239, 68, 68, 0.12)',
                    color: '#ef4444',
                  }}
                >
                  {(challenge as any).organizationName || (challenge as any).organizationId}
                </span>
              )}
            </div>
          </div>

          {/* Progress column */}
          {isActive && (
            <div
              style={{
                flex: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                minWidth: 0,
                flexShrink: 0,
              }}
            >
              {renderCombinedCircularProgress()}
              {/* Progress text centered under the circles */}
              <div
                style={{
                  marginTop: 4,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? '10px' : theme.typography.caption.fontSize,
                    fontFamily: theme.fonts.secondary,
                    color: theme.colors.textSecondary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ color: theme.colors.success, fontWeight: 600 }}>
                    {Math.round(animatedTodayProgress)}%
                  </span>{' '}
                  <span>today</span>
                  <span style={{ margin: '0 4px', opacity: 0.6 }}>•</span>
                  <span style={{ color: theme.colors.primary, fontWeight: 600 }}>
                    {Math.round(animatedOverallProgress)}%
                  </span>{' '}
                  <span>overall</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Meta Row: tags + actions */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isMobile ? '8px' : '0',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '12px',
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
          }}>
            {/* Challenge Tag */}
            <ChallengeTag
              level={challenge.level}
              isEligible={challenge.isEligible}
            />

            {/* Eligibility Status */}
            {!challenge.isEligible && (
              <span style={{
                color: theme.colors.error,
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 500,
                fontFamily: theme.fonts.secondary,
                letterSpacing: theme.typography.caption.letterSpacing,
              }}>
                Not eligible
              </span>
            )}

          </div>

          {/* Primary Actions */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '8px',
            flexWrap: 'nowrap',
            flexShrink: 0,
          }}>
            <button
              style={{
                border: 'none',
                borderRadius: '999px',
                padding: isMobile ? '6px 12px' : '8px 18px',
                fontSize: isMobile ? '11px' : theme.typography.button.fontSize,
                fontWeight: theme.typography.button.fontWeight,
                fontFamily: theme.fonts.primary,
                lineHeight: theme.typography.button.lineHeight,
                letterSpacing: theme.typography.button.letterSpacing,
                cursor: 'pointer',
                backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#ffffff',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(102, 126, 234, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
              }}
              onClick={handleButtonClick}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                  animation: 'shine 2s infinite',
                }}
              />
              <span style={{ position: 'relative', zIndex: 1 }}>
                {isActive ? 'Progress' : 'Join Challenge'}
              </span>
            </button>

            {/* Video Play Button - compact icon pill */}
            {((challenge as any).videoUrl || (challenge as any).audioUrl) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVideoPlay();
                }}
                className="inline-flex items-center justify-center bg-gradient-to-r from-indigo-500 to-sky-500 dark:from-indigo-500 dark:to-sky-500 rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-base text-white"
                style={{
                  width: isMobile ? '36px' : '36px',
                  height: isMobile ? '36px' : '36px',
                  flexShrink: 0
                }}
                title="Watch Video"
              >
                <span style={{ transform: 'translateX(1px)', fontSize: isMobile ? '14px' : '16px' }}>▶️</span>
              </button>
            )}

            {/* Photo Wall Button - compact icon pill, show for active challenges */}
            {isActive && (
              <button
                onClick={handlePhotoWallClick}
                className="inline-flex items-center justify-center bg-gradient-to-r from-purple-500 to-violet-600 dark:from-purple-600 dark:to-violet-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base text-white"
                style={{
                  width: isMobile ? '36px' : '36px',
                  height: isMobile ? '36px' : '36px',
                  flexShrink: 0
                }}
                title="View Challenge Photos"
              >
                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>📷</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      {!onVideoPlayOverride && showVideoPlayer && ((challenge as any).videoUrl || (challenge as any).audioUrl) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {(challenge as any).videoUrl ? (
            <VideoPlayer
              videoUrl={(challenge as any).videoUrl}
              onClose={handleVideoClose}
              autoPlay={true}
              showControls={true}
              className="w-full h-full"
            />
          ) : (
            <div style={{
              backgroundColor: '#000000',
              padding: '24px',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
              color: '#ffffff',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                  Challenge Audio
                </h3>
                <button
                  onClick={handleVideoClose}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '20px',
                    cursor: 'pointer',
                  }}
                  aria-label="Close audio"
                >
                  ×
                </button>
              </div>
              <p style={{ marginBottom: '12px', fontSize: '14px', opacity: 0.85 }}>
                Listen to the challenge instructions or motivation.
              </p>
              <audio
                src={(challenge as any).audioUrl}
                controls
                autoPlay
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ChallengeCard; 